import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { LifeBuoy, Mail, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { t } from '../../lib/i18n';
import { supportAPI } from '../../lib/api';

const SUPPORT_EMAIL = 'support@primeface.io';
const MIN_MESSAGE = 10;
const MAX_MESSAGE = 3000;

export const SupportCard = ({ user, language }) => {
  const isGuest = Boolean(user?.is_guest) || (user?.email || '').endsWith('@primeface.app');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastRef, setLastRef] = useState(null);

  const valid = subject.trim().length >= 3 && message.trim().length >= MIN_MESSAGE && (!isGuest || contactEmail.includes('@'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    try {
      const res = await supportAPI.contact({ subject: subject.trim(), message: message.trim(), contactEmail: contactEmail.trim(), language });
      setLastRef(res.data.ticket_ref);
      setSubject('');
      setMessage('');
      toast.success(`${t('contactSent', language)} #${res.data.ticket_ref}`);
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 429 ? t('contactTooMany', language) : t('contactError', language));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="support-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <LifeBuoy className="w-5 h-5 text-[var(--lux-gold)]" />
          {t('supportTitle', language)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-white/60">{t('supportIntro', language)}</p>

        <form onSubmit={handleSubmit} className="space-y-3" data-testid="support-contact-form">
          {isGuest && (
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">{t('contactEmail', language)}</Label>
              <Input
                data-testid="support-contact-email-input"
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
                placeholder={t('contactEmailPlaceholder', language)}
                className="bg-black/20 border-white/10 text-white placeholder:text-white/30 h-10 focus-visible:ring-[var(--lux-gold)]"
                required
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-white/60 text-xs">{t('contactSubject', language)}</Label>
            <Input
              data-testid="support-subject-input"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder={t('contactSubjectPlaceholder', language)}
              maxLength={150}
              className="bg-black/20 border-white/10 text-white placeholder:text-white/30 h-10 focus-visible:ring-[var(--lux-gold)]"
              required
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-white/60 text-xs">{t('contactMessage', language)}</Label>
              <span className="text-[10px] text-white/30 tabular-nums" data-testid="support-message-counter">{message.length}/{MAX_MESSAGE}</span>
            </div>
            <Textarea
              data-testid="support-message-input"
              value={message}
              onChange={e => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              placeholder={t('contactMessagePlaceholder', language)}
              rows={4}
              className="bg-black/20 border-white/10 text-white placeholder:text-white/30 resize-none focus-visible:ring-[var(--lux-gold)]"
              required
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              data-testid="support-submit-button"
              disabled={!valid || sending}
              className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? t('contactSending', language) : t('contactSend', language)}
            </Button>
            {lastRef && (
              <span className="inline-flex items-center gap-1.5 text-xs text-[var(--lux-amber)]" data-testid="support-last-ref">
                <CheckCircle2 className="w-3.5 h-3.5" /> {t('contactSent', language)} #{lastRef}
              </span>
            )}
          </div>
        </form>

        <div className="flex items-center gap-2 text-xs text-white/40 pt-1">
          <span>{t('contactOrEmail', language)}</span>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=PrimeFace%20Support`}
            data-testid="support-email-link"
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200"
          >
            <Mail className="w-3.5 h-3.5 text-[var(--lux-gold)]" />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </CardContent>
    </Card>
  );
};
